// ©2024 Austin App House. All rights reserved.
import React, {useCallback, useEffect, useRef, useState} from "react";
import axios from "axios";

export const useDataTable = (entity, options = {}) => {
  const [pending, setPending] = React.useState(true);
  const [initial, setInitial] = useState(true)
  const latestRequest = useRef(0)
  const [data, setData] = useState({})
  const [page, setPage] = useState(options.page ?? 1)
  const [startDate, setStartDate] = useState(options.startDate ?? 0)
  const [endDate, setEndDate] = useState(options.endDate ?? 0)
  const [companyName, setCompanyName] = useState(options.companyName ?? '')
  const [perPage, setPerPage] = useState(options.perPage ?? 5)
  const [sortBy, setSortBy] = useState(options.sortBy ?? 'updatedAt')
  const [sortDirection, setSortDirection] = useState(options.sortDirection ?? -1)
  const [filters, setFilters] = useState(options.filters ?? {})

  const { columns, isReload, refreshToken } = options

  const params = {
    page,
    sortBy,
    sortDirection,
    perPage,
    startDate,
    endDate,
    companyName,
    filters
  }

  const refetch = useCallback(() => {
    const request = ++latestRequest.current
    setPending(true)
    axios.get(entity, {params})
      .then(res => res.data)
      .then(result => {
        if (request !== latestRequest.current) return
        // Opt-in refresh preserves table choices, but an emptied last page
        // must move back into the remaining result set.
        if (refreshToken !== undefined && Array.isArray(result.payload) &&
            result.payload.length === 0 && Number.isInteger(result.total) && result.total >= 0) {
          const lastPage = Math.max(1, Math.ceil(result.total / perPage))
          if (page > lastPage) setPage(lastPage)
        }
        setData(result)
      })
      .catch(() => {
        if (request === latestRequest.current) setData({})
      })
  }, [entity, params])

  useEffect(() => () => { latestRequest.current += 1 }, [])

  const context = {
    refetch,
    columns,
    data,
    page,
    setPage,
    sortBy,
    setSortBy,
    sortDirection,
    setSortDirection,
    perPage,
    setPerPage,
    setFilters,
    setStartDate,
    setEndDate,
    setCompanyName,
    pending
  }

  useEffect(() => {
    if (initial) {
      setInitial(false)
    }
  }, [initial])

  useEffect(() => {
    if (!options.actions) {
      return undefined;
    }
    columns.push({
      button: true,
      width: '150px',
      center: true,
      cell: options.actions
    })
    return undefined;
  }, [options.actions])

  useEffect(() => {
    if (initial) {
      return undefined;
    }
    setPending(false)
    return undefined;
  }, [data, initial])

  useEffect(() => {
    if (initial) {
      return undefined;
    }
    setPage(1)
    return undefined;
  }, [sortBy, sortDirection, perPage, filters, initial])

  useEffect(() => {
    refetch()
  }, [page, perPage, filters, sortBy, sortDirection, isReload, refreshToken])

  return {...context, context}
}